import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Award, Target } from 'lucide-react';

interface PerformanceSnapshot {
  id: string;
  season_name: string;
  season_year: number;
  snapshot_date: string;
  total_games: number;
  win_rate: number;
  average_score: number;
  ranking_position: number | null;
  performance_metrics: any;
  achievements: any;
}

interface HistoricalPerformanceProps {
  entityId: string;
  entityType: 'player' | 'team' | 'coach' | 'venue';
}

export const HistoricalPerformance = ({ 
  entityId, 
  entityType 
}: HistoricalPerformanceProps) => {
  const [snapshots, setSnapshots] = useState<PerformanceSnapshot[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPerformanceHistory();
  }, [entityId, entityType]);

  const loadPerformanceHistory = async () => {
    try {
      const { data, error } = await supabase
        .from('historical_performance_snapshots')
        .select('*')
        .eq('entity_id', entityId)
        .eq('entity_type', entityType)
        .order('season_year', { ascending: false })
        .order('snapshot_date', { ascending: false });

      if (error) throw error;
      setSnapshots((data as any[]) || []);
    } catch (error) {
      console.error('Error loading performance history:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTrend = (current: number, previous: number) => {
    if (!previous) return null;
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change).toFixed(1),
      direction: change >= 0 ? 'up' : 'down'
    };
  };

  const getTrendIcon = (direction: string) => {
    return direction === 'up' ? (
      <TrendingUp className="w-4 h-4 text-green-600" />
    ) : (
      <TrendingDown className="w-4 h-4 text-red-600" />
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">Loading performance history...</p>
        </CardContent>
      </Card>
    );
  }

  if (snapshots.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Target className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No performance history yet</p>
          <p className="text-sm text-muted-foreground mt-2">
            Historical performance data will appear here as seasons progress
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">Performance History</h3>
        <p className="text-sm text-muted-foreground">
          Track your progress across seasons
        </p>
      </div>

      <div className="space-y-4">
        {snapshots.map((snapshot, index) => {
          const previousSnapshot = snapshots[index + 1];
          const winRateTrend = previousSnapshot ? 
            calculateTrend(snapshot.win_rate, previousSnapshot.win_rate) : null;

          return (
            <Card key={snapshot.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">
                      {snapshot.season_name}
                    </CardTitle>
                    <CardDescription>
                      {snapshot.season_year} Season
                    </CardDescription>
                  </div>
                  {snapshot.ranking_position && (
                    <Badge variant="secondary">
                      <Award className="w-3 h-3 mr-1" />
                      Rank #{snapshot.ranking_position}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="font-semibold text-lg">
                      {snapshot.total_games}
                    </div>
                    <div className="text-xs text-muted-foreground">Games</div>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="font-semibold text-lg flex items-center justify-center gap-1">
                      {snapshot.win_rate.toFixed(1)}%
                      {winRateTrend && getTrendIcon(winRateTrend.direction)}
                    </div>
                    <div className="text-xs text-muted-foreground">Win Rate</div>
                    {winRateTrend && (
                      <div className={`text-xs ${
                        winRateTrend.direction === 'up' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {winRateTrend.direction === 'up' ? '+' : '-'}{winRateTrend.value}%
                      </div>
                    )}
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="font-semibold text-lg">
                      {snapshot.average_score.toFixed(1)}
                    </div>
                    <div className="text-xs text-muted-foreground">Avg Score</div>
                  </div>
                </div>

                {snapshot.achievements && Array.isArray(snapshot.achievements) && snapshot.achievements.length > 0 && (
                  <div className="pt-3 border-t">
                    <p className="text-xs font-medium mb-2">Achievements</p>
                    <div className="flex flex-wrap gap-2">
                      {snapshot.achievements.slice(0, 3).map((achievement: any, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          <Award className="w-3 h-3 mr-1" />
                          {achievement.name || achievement.title}
                        </Badge>
                      ))}
                      {snapshot.achievements.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{snapshot.achievements.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
